import { useMemo, useState } from 'react';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import type { GridColDef } from '@mui/x-data-grid';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { DataTable, LoadingState } from '@radiology/ui';
import dayjs from 'dayjs';
import {
  COLOR_LABEL,
  COLOR_TONE,
  DUE_STATUSES,
  DUE_TONE,
  useDevices,
  type Device,
  type DueStatus,
} from './api';

/**
 * One cell carrying a due status, its date and who last performed it.
 *
 * ST-BR-04 / AC-13 — Due Soon and Overdue must be visually distinguishable, so
 * the chip is coloured rather than the status merely being written out.
 */
function dueCell(status: DueStatus, next: string | null, by: string | null) {
  return (
    <Stack sx={{ py: 0.5 }} gap={0.25}>
      <Chip
        size="small"
        label={status}
        color={DUE_TONE[status] === 'default' ? undefined : DUE_TONE[status]}
        variant={DUE_TONE[status] === 'default' ? 'outlined' : 'filled'}
        sx={{ alignSelf: 'flex-start' }}
      />
      <Typography variant="caption" color="text.secondary">
        {next ? dayjs(next).format('D MMM YYYY') : 'no date'}
        {by ? ` · ${by}` : ''}
      </Typography>
    </Stack>
  );
}

/**
 * The one device table, shared by every role's screens.
 *
 * `allowExport` replaces the legacy `GET /api/device/export-data`, which built an
 * xlsx on the server from an unfiltered `findAll()`. Exporting what RLS returned
 * is both simpler and narrower.
 */
export function DeviceTable({
  filter,
  emptyTitle,
  emptyDescription,
  actionLabel,
  onAction,
}: {
  filter?: 'all' | 'obsolete' | 'active';
  emptyTitle: string;
  emptyDescription?: string;
  actionLabel?: string;
  onAction?: (device: Device) => void;
}) {
  // ST-BR-05 / AC-14 — filter by QC and PM status. Applied in the database on
  // the computed status, so the filter and the column can never disagree.
  const [qcStatus, setQcStatus] = useState<DueStatus | ''>('');
  const [pmStatus, setPmStatus] = useState<DueStatus | ''>('');
  const { data, isLoading, error, refetch } = useDevices(filter, {
    qcStatus: qcStatus || undefined,
    pmStatus: pmStatus || undefined,
  });
  const [search, setSearch] = useState('');

  // Searches every device the query returned, not the page on screen: useDevices
  // pages through the whole table in 1,000-row batches and this filter runs over
  // all of it, before the grid paginates what is left.
  const rows = useMemo(() => {
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    if (!needle) return data;
    return data.filter(d =>
      [
        d.equipment_name,
        d.htm ?? '',
        d.model ?? '',
        d.serial_no ?? '',
        d.manufacturer ?? '',
        d.section ?? '',
        d.location ?? '',
        d.site_name ?? '',
      ]
        .filter(Boolean)
        .some(v => v.toLowerCase().includes(needle)),
    );
  }, [data, search]);

  const columns = useMemo<GridColDef<Device>[]>(() => {
    const cols: GridColDef<Device>[] = [
      {
        field: 'equipment_name',
        headerName: 'Equipment',
        flex: 1.5,
        minWidth: 220,
        renderCell: params => (
          <Stack sx={{ py: 0.5 }}>
            <Typography variant="body2">{params.row.equipment_name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {[params.row.manufacturer, params.row.model].filter(Boolean).join(' · ') || '—'}
              {params.row.serial_no ? ` · S/N ${params.row.serial_no}` : ''}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'site_name',
        headerName: 'Where',
        flex: 1,
        minWidth: 160,
        renderCell: params => (
          <Stack sx={{ py: 0.5 }}>
            <Typography variant="body2">{params.row.site_name || '—'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {[params.row.section, params.row.location].filter(Boolean).join(' · ') || '—'}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'years_in_service',
        headerName: 'Age',
        width: 130,
        renderCell: params => {
          const years = params.row.years_in_service;
          if (years === null) return <Typography variant="body2">—</Typography>;
          return (
            <Stack sx={{ py: 0.5 }}>
              {/* Computed by the view on every read. The legacy stored value is
                  wrong on 69 of the 154 migrated rows. */}
              <Typography variant="body2" color={years >= 10 ? 'warning.main' : undefined}>
                {years} {years === 1 ? 'year' : 'years'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {params.row.installation_date
                  ? dayjs(params.row.installation_date).format('MMM YYYY')
                  : ''}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: 'color_category',
        headerName: 'Condition',
        width: 130,
        renderCell: params => {
          const code = params.row.color_category ?? '';
          const tone = COLOR_TONE[code];
          if (!tone)
            return (
              <Typography variant="caption" color="text.secondary">
                —
              </Typography>
            );
          return <Chip size="small" label={COLOR_LABEL[code]} color={tone} />;
        },
      },
      {
        field: 'is_obsolete',
        headerName: 'Status',
        width: 120,
        renderCell: params =>
          params.row.is_obsolete ? (
            <Chip size="small" label="Obsolete" color="error" variant="outlined" />
          ) : (
            <Chip size="small" label="In service" color="success" variant="outlined" />
          ),
      },
      // Section 7 — QC and PM, each as one column carrying the whole story:
      // status, next date and who last performed it. Eight separate columns
      // would not fit beside the equipment details.
      {
        field: 'qc_status',
        headerName: 'QC',
        width: 165,
        renderCell: params =>
          dueCell(params.row.qc_status, params.row.next_qc_date, params.row.qc_performed_by),
      },
      {
        field: 'pm_status',
        headerName: 'PM',
        width: 165,
        renderCell: params =>
          dueCell(params.row.pm_status, params.row.next_pm_date, params.row.pm_performed_by),
      },
      {
        field: 'downtime_minutes',
        headerName: 'Downtime',
        width: 120,
        align: 'right',
        headerAlign: 'right',
        renderCell: params =>
          params.row.downtime_count > 0 ? (
            <Stack sx={{ py: 0.5, alignItems: 'flex-end' }}>
              <Typography variant="body2">{params.row.downtime_minutes} min</Typography>
              <Typography variant="caption" color="text.secondary">
                {params.row.downtime_count}{' '}
                {params.row.downtime_count === 1 ? 'incident' : 'incidents'}
              </Typography>
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">
              none
            </Typography>
          ),
      },
    ];

    if (onAction) {
      cols.push({
        field: 'actions',
        headerName: '',
        width: 110,
        sortable: false,
        filterable: false,
        align: 'right',
        headerAlign: 'right',
        renderCell: params => (
          <Button size="small" variant="outlined" onClick={() => onAction(params.row)}>
            {actionLabel ?? 'Open'}
          </Button>
        ),
      });
    }

    return cols;
  }, [onAction, actionLabel]);

  const statusFilter = (
    label: string,
    value: DueStatus | '',
    onChange: (v: DueStatus | '') => void,
  ) => (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      onChange={e => onChange(e.target.value as DueStatus | '')}
      sx={{ minWidth: 150 }}
    >
      <MenuItem value="">Any</MenuItem>
      {DUE_STATUSES.map(s => (
        <MenuItem key={s} value={s}>
          {s}
        </MenuItem>
      ))}
    </TextField>
  );

  if (isLoading) return <LoadingState variant="skeleton" rows={6} />;

  return (
    <DataTable<Device>
      rows={rows}
      columns={columns}
      getRowHeight={() => 56}
      search={{
        value: search,
        onChange: setSearch,
        placeholder: 'Search equipment, HTM, model, serial, section…',
      }}
      toolbarActions={
        <>
          {statusFilter('QC status', qcStatus, setQcStatus)}
          {statusFilter('PM status', pmStatus, setPmStatus)}
        </>
      }
      empty={{ title: emptyTitle, description: emptyDescription }}
      error={error ? { message: error.message, onRetry: () => void refetch() } : undefined}
      allowExport
    />
  );
}
